import { type SetupRosterPreviewTarget } from '@onestep/host-frame-protocol/org-setup';
import { omit } from 'lodash';
import { useQueryStates } from 'nuqs';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  MAIN_CHAT_CATEGORY,
  setupChatCategoryOf,
  setupChatPanelParser,
  type SetupChatTarget,
} from '@/services/orgSetupService/setupChatTarget';

import { createRememberedSetupChats } from './rememberedSetupChats';

const setupChatParams = { setup_chat: setupChatPanelParser };

type PanelChat = { orgRef: string; workspace: string; chatCategory: string | null };

const panelChatOf = (target: SetupChatTarget): PanelChat => ({
  orgRef: target.orgRef,
  workspace: target.workspace,
  chatCategory: target.chatCategory ?? setupChatCategoryOf(target),
});

const useSetupChatPanelStateValue = () => {
  const [{ setup_chat: setupChat }, setSetupChatParams] = useQueryStates(setupChatParams);
  const [rememberedChats] = useState(() => {
    const initial = setupChat && panelChatOf(setupChat);
    return createRememberedSetupChats(
      setupChat?.sessionId && initial?.chatCategory
        ? { ...initial, sessionId: setupChat.sessionId, chatCategory: initial.chatCategory }
        : undefined,
    );
  });
  const lastPanelChat = useRef<PanelChat | null>(null);
  const isPanelClosed = useRef(!setupChat);
  useEffect(() => {
    isPanelClosed.current = !setupChat;
    if (setupChat) lastPanelChat.current = panelChatOf(setupChat);
  }, [setupChat]);
  const [setupChatGeneration, setSetupChatGeneration] = useState(0);
  const [requestedSetupPreview, setRequestedSetupPreview] =
    useState<SetupRosterPreviewTarget | null>(null);
  const [shownSetupPreview, setShownSetupPreview] = useState<SetupRosterPreviewTarget | null>(null);

  const setupPreviewTarget = setupChat ? requestedSetupPreview : null;
  const setupPreviewShown =
    setupPreviewTarget &&
    shownSetupPreview?.workingCopyId === setupPreviewTarget.workingCopyId &&
    shownSetupPreview.proposalRevision === setupPreviewTarget.proposalRevision
      ? shownSetupPreview
      : null;

  const clearSetupPreview = useCallback(() => {
    setRequestedSetupPreview(null);
    setShownSetupPreview(null);
  }, []);
  if (!setupChat && (requestedSetupPreview || shownSetupPreview)) clearSetupPreview();

  const resolveSetupChatTarget = useCallback(
    (target: SetupChatTarget): SetupChatTarget => {
      const chatCategory =
        target.chatCategory ??
        (target.sessionId ? MAIN_CHAT_CATEGORY : setupChatCategoryOf(target));
      if (!chatCategory) return target;
      if (target.sessionId) {
        rememberedChats.remember(target, { sessionId: target.sessionId, chatCategory });
        return { ...target, chatCategory };
      }
      const last = chatCategory === MAIN_CHAT_CATEGORY ? rememberedChats.lastOf(target) : undefined;
      const sessionId = last ? last.sessionId : rememberedChats.ofCategory(target, chatCategory);
      if (!sessionId) return { ...target, chatCategory };
      const resumedCategory = last?.chatCategory ?? chatCategory;
      rememberedChats.remember(target, { sessionId, chatCategory: resumedCategory });
      return { ...omit(target, 'start'), sessionId, chatCategory: resumedCategory };
    },
    [rememberedChats],
  );
  const openSetupChat = useCallback(
    (target: SetupChatTarget) => {
      isPanelClosed.current = false;
      void setSetupChatParams({ setup_chat: resolveSetupChatTarget(target) }, { history: 'push' });
    },
    [resolveSetupChatTarget, setSetupChatParams],
  );
  const closeSetupChat = useCallback(() => {
    isPanelClosed.current = true;
    void setSetupChatParams({ setup_chat: null });
  }, [setSetupChatParams]);
  const setupChatOpened = useCallback(
    (sessionId: string, workspace?: string) => {
      const panel = lastPanelChat.current;
      if (panel?.chatCategory)
        rememberedChats.remember(
          { orgRef: panel.orgRef, workspace: workspace ?? panel.workspace },
          { sessionId, chatCategory: panel.chatCategory },
        );
      if (isPanelClosed.current) return;
      void setSetupChatParams(
        (current) =>
          current.setup_chat
            ? {
                setup_chat: {
                  ...omit(current.setup_chat, 'start'),
                  workspace: workspace ?? current.setup_chat.workspace,
                  sessionId,
                },
              }
            : {},
        { history: 'replace' },
      );
    },
    [rememberedChats, setSetupChatParams],
  );
  const restartSetupChat = useCallback(async () => {
    if (!setupChat) return;
    const { chatCategory } = panelChatOf(setupChat);
    if (chatCategory) rememberedChats.forget(setupChat, chatCategory);
    await setSetupChatParams(
      {
        setup_chat: {
          orgRef: setupChat.orgRef,
          workspace: setupChat.workspace,
          entry: { kind: 'main' },
          ...(chatCategory && { chatCategory }),
        },
      },
      { history: 'replace' },
    );
    setSetupChatGeneration((current) => current + 1);
    clearSetupPreview();
  }, [setupChat, rememberedChats, setSetupChatParams, clearSetupPreview]);

  return useMemo(
    () => ({
      setupChat,
      setupChatGeneration,
      resolveSetupChatTarget,
      openSetupChat,
      closeSetupChat,
      setupChatOpened,
      restartSetupChat,
      requestedSetupPreview,
      setupPreviewTarget,
      setupPreviewShown,
      showSetupPreview: setRequestedSetupPreview,
      markSetupPreviewShown: setShownSetupPreview,
      clearSetupPreview,
    }),
    [
      setupChat,
      setupChatGeneration,
      resolveSetupChatTarget,
      openSetupChat,
      closeSetupChat,
      setupChatOpened,
      restartSetupChat,
      requestedSetupPreview,
      setupPreviewTarget,
      setupPreviewShown,
      clearSetupPreview,
    ],
  );
};

type SetupChatPanelState = ReturnType<typeof useSetupChatPanelStateValue>;

const SetupChatPanelStateContext = createContext<SetupChatPanelState | null>(null);

export const SetupChatPanelStateProvider = ({ children }: { children: ReactNode }) => {
  const state = useSetupChatPanelStateValue();
  return (
    <SetupChatPanelStateContext.Provider value={state}>
      {children}
    </SetupChatPanelStateContext.Provider>
  );
};

export const useSetupChatPanelState = (): SetupChatPanelState => {
  const state = useContext(SetupChatPanelStateContext);
  if (!state)
    throw new Error('useSetupChatPanelState must be used inside SetupChatPanelStateProvider');
  return state;
};
